import {
  API_CONFIG,
  getHeaders,
  ApiResponse,
  PaginatedResponse,
  RoomData,
  RoomFilters,
  RoomSearchParams,
} from '../config/settings'
import { formatApiError, isRetryableApiError, sleep } from '../utils/formatApiError'

class ApiService {
  private baseURL: string

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL
  }

  private async requestOnce<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`

    const config: RequestInit = {
      ...options,
      headers: {
        ...getHeaders(options.method !== 'GET'),
        ...options.headers,
      },
    }

    const response = await fetch(url, config)

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`
      try {
        const errBody = await response.json()
        if (errBody?.detail) {
          message = typeof errBody.detail === 'string' ? errBody.detail : message
        } else if (errBody?.message) {
          message = String(errBody.message)
        }
      } catch {
        /* ignore non-JSON error bodies */
      }
      throw new Error(formatApiError(message))
    }

    const data = await response.json()
    return {
      success: true,
      data,
      message: data.message,
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const isGet = !options.method || options.method === 'GET'
    const attempts = isGet ? API_CONFIG.RETRY_ATTEMPTS : 1

    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        return await this.requestOnce<T>(endpoint, options)
      } catch (error) {
        const raw = error instanceof Error ? error.message : 'An unknown error occurred'
        const message = formatApiError(raw)
        const retry = isGet && attempt < attempts && isRetryableApiError(message)
        if (retry) {
          await sleep(1200 * attempt)
          continue
        }
        return {
          success: false,
          data: null as T,
          message,
        }
      }
    }

    return {
      success: false,
      data: null as T,
      message: 'Request failed after multiple attempts.',
    }
  }

  private addQueryParam(queryParams: URLSearchParams, key: string, value: unknown): void {
    if (value === undefined || value === null || value === '') {
      return
    }

    if (typeof value === 'number' && isNaN(value)) {
      return
    }

    if (key === 'page') {
      const pageNum = typeof value === 'number' ? value : parseInt(String(value), 10)
      if (isNaN(pageNum) || pageNum < 1) {
        return
      }
      queryParams.append(key, pageNum.toString())
      return
    }

    if (Array.isArray(value)) {
      queryParams.append(key, value.join(','))
      return
    }

    queryParams.append(key, value.toString())
  }

  async getRooms(filters?: RoomFilters): Promise<ApiResponse<PaginatedResponse<RoomData>>> {
    const queryParams = new URLSearchParams()

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        this.addQueryParam(queryParams, key, value)
      })
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.ROOMS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return this.request<PaginatedResponse<RoomData>>(endpoint)
  }

  async getRoomById(id: number): Promise<ApiResponse<RoomData>> {
    return this.request<RoomData>(`${API_CONFIG.ENDPOINTS.ROOM_DETAILS}/${id}`)
  }

  async searchRooms(searchParams: RoomSearchParams): Promise<ApiResponse<PaginatedResponse<RoomData>>> {
    const queryParams = new URLSearchParams()

    Object.entries(searchParams).forEach(([key, value]) => {
      this.addQueryParam(queryParams, key, value)
    })

    const endpoint = `${API_CONFIG.ENDPOINTS.ROOM_SEARCH}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return this.request<PaginatedResponse<RoomData>>(endpoint)
  }

  async getRoomsByCategory(
    categoryId: string,
    filters?: RoomFilters,
  ): Promise<ApiResponse<PaginatedResponse<RoomData>>> {
    const queryParams = new URLSearchParams()

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        this.addQueryParam(queryParams, key, value)
      })
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.ROOM_CATEGORY}/${categoryId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return this.request<PaginatedResponse<RoomData>>(endpoint)
  }

  async createBooking(bookingData: unknown): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(API_CONFIG.ENDPOINTS.BOOKINGS, {
      method: 'POST',
      body: JSON.stringify(bookingData),
    })
  }

  async getBooking(id: number): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(`${API_CONFIG.ENDPOINTS.BOOKINGS}/${id}`)
  }

  async login(credentials: { email: string; password: string }): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async register(userData: unknown): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(API_CONFIG.ENDPOINTS.AUTH.REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async logout(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(API_CONFIG.ENDPOINTS.AUTH.LOGOUT, {
      method: 'POST',
    })
  }

  async getCurrentUser(): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(API_CONFIG.ENDPOINTS.AUTH.USER)
  }
}

export const apiService = new ApiService()
export default apiService
